import { useState, useEffect } from 'react';
import { api } from '../api';
import type { DomainDetail } from '../types';
import { useScanStatus } from '../hooks/useScanStatus';
import { ScanInProgressBanner } from '../components/ScanInProgressBanner';
import { OverviewTab } from './tabs/OverviewTab';
import { ContentTab } from './tabs/ContentTab';
import { SuggestionsTab } from './tabs/SuggestionsTab';
import { AnalyticsTab } from './AnalyticsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { ActivityTab } from './tabs/ActivityTab';
import { CompetitorsTab } from './tabs/CompetitorsTab';
import { AiInterrogationTab } from './tabs/AiInterrogationTab';
import { AiTruthTab } from './tabs/AiTruthTab';
import { OutreachPipeline } from '../sections/OutreachPipeline';
import { ContentAndKeywords } from '../sections/ContentAndKeywords';
import { ProgressTab } from '../components/ProgressTab';

type Tab = 'overview' | 'analytics' | 'progress' | 'suggestions' | 'content' | 'activity' | 'competitors' | 'interrogation' | 'ai-truth' | 'settings' | 'outreach' | 'keywords';

interface Props {
  domainId: string;
  tier: string;
  onBack: () => void;
  onStartOnboarding?: () => void;
  onAiTruthSetup?: (domain: import('../types').DomainDetail) => void;
  initialTab?: string;
}

function LockedTab({ minTier }: { minTier: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xl">🔒</div>
      <div className="text-center">
        <p className="font-medium text-brand-ink">Requires {minTier} plan</p>
        <p className="text-sm text-gray-400 mt-1">Upgrade to access this feature and improve your SEO.</p>
      </div>
      <button
        onClick={async () => {
          const tierSlug = ({ starter: 'tier_1', growth: 'tier_2', scale: 'tier_3' } as const)[minTier as 'starter' | 'growth' | 'scale'] ?? 'tier_1';
          const { url } = await api.billing.checkout(tierSlug);
          window.location.href = url;
        }}
        className="bg-brand-green text-white font-medium px-6 py-2.5 rounded-lg text-sm hover:bg-brand-green-dark transition-colors"
      >
        Upgrade to {minTier.charAt(0).toUpperCase() + minTier.slice(1)}
      </button>
    </div>
  );
}

export function DomainDetailPage({ domainId, tier, onBack, onStartOnboarding, onAiTruthSetup, initialTab }: Props) {
  const [domain, setDomain] = useState<DomainDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>((initialTab as Tab | undefined) ?? 'overview');
  const [faqGeneratorPrefillUrl, setFaqGeneratorPrefillUrl] = useState<string | undefined>();

  function handleNavigateToFaqGenerator(targetUrl?: string) {
    setFaqGeneratorPrefillUrl(targetUrl);
    setTab('content');
  }

  function handleNavigateToOutreachPipeline() {
    setTab('outreach');
  }

  function handleNavigateToContentKeywords() {
    setTab('keywords');
  }

  useEffect(() => {
    api.domains
      .get(domainId)
      .then(setDomain)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load domain'))
      .finally(() => setLoading(false));
  }, [domainId]);

  const scanStatus = useScanStatus(domainId);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !domain) {
    return (
      <div className="min-h-screen bg-brand-paper flex flex-col items-center justify-center gap-4">
        <p className="text-red-500 text-sm">{error || 'Domain not found'}</p>
        <button onClick={onBack} className="text-sm text-brand-green hover:underline">← Back</button>
      </div>
    );
  }

  const TIER_RANK: Record<string, number> = { free: 0, tier_1: 1, tier_2: 2, tier_3: 3 };
  const userRank = TIER_RANK[tier] ?? 0;

  const tabs: { id: Tab; label: string; minRank: number }[] = [
    { id: 'overview',     label: 'Overview',     minRank: 0 },
    { id: 'analytics',   label: 'Audits',       minRank: 0 },
    { id: 'progress',    label: 'Progress',     minRank: 0 },
    { id: 'suggestions', label: 'Suggestions',  minRank: 1 },
    { id: 'content',     label: 'Content',      minRank: 1 },
    { id: 'competitors',    label: 'Competitors',   minRank: 1 },
    { id: 'interrogation', label: 'AI Interrogation', minRank: 2 },
    { id: 'ai-truth',      label: 'AI Truth',         minRank: 1 },
    { id: 'activity',      label: 'Activity',          minRank: 1 },
    { id: 'outreach',    label: 'Outreach',     minRank: 1 },
    { id: 'keywords',    label: 'Keywords',     minRank: 1 },
    { id: 'settings',    label: 'Settings',     minRank: 0 },
  ];

  return (
    <div className="min-h-screen bg-brand-paper">
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-20">
        <button
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-brand-ink mb-4 inline-flex items-center gap-1"
        >
          ← Domains
        </button>

        {/* Domain header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-brand-ink">{domain.domain}</h1>
            <p className="text-sm text-gray-400 mt-0.5 capitalize">{domain.state}</p>
          </div>
          {domain.verified ? (
            <span className="text-xs bg-green-50 text-brand-green px-3 py-1 rounded-full font-medium">Verified</span>
          ) : (
            <span className="text-xs bg-amber-50 text-amber-600 px-3 py-1 rounded-full font-medium">Pending</span>
          )}
        </div>

        {/* Setup incomplete banner */}
        {!domain.onboardingCompletedAt && onStartOnboarding && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-amber-800">Setup not complete</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Finish the setup wizard to add your location, target audience, and goals — this unlocks your first audit.
              </p>
            </div>
            <button
              onClick={onStartOnboarding}
              className="shrink-0 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg transition-colors"
            >
              Continue setup →
            </button>
          </div>
        )}

        {/* Scan in-progress banner — long-scan paid audits only */}
        {scanStatus.scanning && (
          <div className="mb-4">
            <ScanInProgressBanner isLongScan={scanStatus.isLongScan} />
          </div>
        )}

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 mb-6 gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
                tab === t.id
                  ? 'border-b-2 border-brand-green text-brand-green bg-white -mb-px'
                  : t.minRank > userRank
                  ? 'text-gray-300'
                  : 'text-gray-500 hover:text-brand-ink'
              }`}
            >
              {t.label}
              {t.minRank > userRank && <span className="ml-1 text-[10px]">🔒</span>}
            </button>
          ))}
        </div>

        {/* Tab panels */}
        {tab === 'overview' && (
          <OverviewTab
            domain={domain}
            onVerified={() => setDomain((d) => (d ? { ...d, verified: true } : d))}
            {...(onAiTruthSetup ? { onAiTruthSetup: () => onAiTruthSetup(domain) } : {})}
          />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab domainId={domain.id} domain={domain.domain} tier={tier} verified={domain.verified} />
        )}
        {tab === 'progress' && (
          <ProgressTab domainId={domain.id} domain={domain.domain} customerTier={tier} />
        )}
        {tab === 'suggestions' && (
          userRank >= 1
            ? <SuggestionsTab
                domainId={domain.id}
                domain={domain.domain}
                onNavigateToFaqGenerator={handleNavigateToFaqGenerator}
                onNavigateToOutreachPipeline={handleNavigateToOutreachPipeline}
                onNavigateToContentKeywords={handleNavigateToContentKeywords}
              />
            : <LockedTab minTier="starter" />
        )}
        {tab === 'content' && (
          userRank >= 1
            ? <ContentTab
                domainId={domain.id}
                domain={domain.domain}
                {...(faqGeneratorPrefillUrl !== undefined ? { faqGeneratorPrefillUrl } : {})}
              />
            : <LockedTab minTier="starter" />
        )}
        {tab === 'interrogation' && (
          userRank >= 2
            ? <AiInterrogationTab domainId={domain.id} domain={domain.domain} />
            : <LockedTab minTier="growth" />
        )}
        {tab === 'activity' && (
          userRank >= 1 ? <ActivityTab domainId={domain.id} /> : <LockedTab minTier="starter" />
        )}
        {tab === 'competitors' && (
          userRank >= 1 ? (
            <CompetitorsTab domainId={domain.id} tier={tier} />
          ) : (
            <LockedTab minTier="starter" />
          )
        )}
        {tab === 'outreach' && (
          userRank >= 1
            ? <OutreachPipeline domainId={domain.id} tier={tier} />
            : <LockedTab minTier="starter" />
        )}
        {tab === 'keywords' && (
          userRank >= 1
            ? <ContentAndKeywords domainId={domain.id} domain={domain.domain} tier={tier} />
            : <LockedTab minTier="starter" />
        )}
        {tab === 'ai-truth' && (
          userRank >= 1
            ? <AiTruthTab
                domainId={domain.id}
                domain={domain.domain}
                tier={tier}
                {...(onAiTruthSetup ? { onSetupWizard: () => onAiTruthSetup(domain) } : {})}
              />
            : <LockedTab minTier="starter" />
        )}
        {tab === 'settings' && (
          <SettingsTab domainId={domain.id} domain={domain.domain} />
        )}

        {/* Footer — glossary link */}
        <footer className="mt-12 pt-6 border-t border-gray-100 text-center">
          <a
            href="/glossary"
            className="text-xs text-gray-400 hover:text-brand-green transition-colors"
          >
            How we measure progress →
          </a>
        </footer>
      </div>

    </div>
  );
}
